/**
 * The cache.
 *
 * `staleTime: Infinity` everywhere: freshness comes from the socket, not from a
 * timer. Anything that refetches on an interval is either duplicating the
 * bridge or papering over a bridge that does not work.
 *
 * A 401 is handled once, here. A tab left open over a weekend will hit one
 * eventually, and the failure mode of handling it per component is half the
 * screen showing an error and the other half showing three-day-old rows.
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ContractError, HTTPError } from './api';
import { keys } from './keys';
import { socket } from './socket';

function isUnauthenticated(error: unknown): boolean {
  return error instanceof HTTPError && error.status === 401;
}

export function createQueryClient(): QueryClient {
  const onError = (error: unknown): void => {
    if (!isUnauthenticated(error)) return;
    socket.disconnect();
    // Not a hard reload: dropping the session marks every guarded route as
    // signed out on the next render, and the login screen keeps the URL it
    // came from so the operator lands back where they were.
    client.setQueryData(keys.me, null);
  };

  const client = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (count, error) => {
          // A 4xx will say the same thing next time, and a response that failed
          // its schema will fail it again. Only transport and 5xx are worth a
          // second attempt.
          if (error instanceof ContractError) return false;
          if (error instanceof HTTPError && error.status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });

  return client;
}
